<!-- Main container for sidebar + content -->
<div class="main-container">
  <!-- Sidebar Navigation (desktop only) -->
  <mat-nav-list class="sidebar desktop-only">
    @for (tab of getVisibleTabs(); track tab.id) {
      @if (tab.subTabs && tab.subTabs.length > 0) {
        <!-- Parent Tab with Sub-menu -->
        <mat-list-item 
          [class.active]="activeParentTab === tab.id"
          [class.expanded]="isParentTabExpanded(tab.id)"
          (click)="openTab(tab.id)">
          <mat-icon>{{ tab.icon }}</mat-icon>
          <span>{{ tab.name }}</span>
          <mat-icon class="expand-icon" [class.expanded]="isParentTabExpanded(tab.id)">
            {{ isParentTabExpanded(tab.id) ? 'expand_less' : 'expand_more' }}
          </mat-icon>
        </mat-list-item>
        
        <!-- Sub-tabs (shown when parent is expanded) -->
        @if (isParentTabExpanded(tab.id)) {
          @for (subTab of getSubTabs(tab.id); track subTab.id) {
            <mat-list-item 
              class="sub-tab-item"
              [class.active]="activeTab === subTab.id"
              (click)="openSubTab(tab.id, subTab.id, $event)">
              <mat-icon>{{ subTab.icon }}</mat-icon>
              <span>{{ subTab.name }}</span>
            </mat-list-item>
          }
        }
      } @else {
        <!-- Regular Tab -->
        <mat-list-item [class.active]="activeTab === tab.id" (click)="openTab(tab.id)">
          <mat-icon>{{ tab.icon }}</mat-icon>
          <span>{{ tab.name }}</span>
        </mat-list-item>
      }
    }
  </mat-nav-list>

    <!-- Content -->
    <div class="content">
    <!-- Active Tab Header (hidden for overview, prompt-tracking, prompt-research, and ai-citations tabs; they use their own header) -->
    @if (activeTab !== 'overview' && activeTab !== 'prompt-tracking' && activeTab !== 'prompt-research' && activeTab !== 'ai-citations') {
      <div class="active-tab-header">
        <div class="active-tab-info">
          <mat-icon>{{ getActiveTabIcon() }}</mat-icon>
          <span class="active-tab-name">{{ getTabTitle(activeTab || 'overview') }}</span>
        </div>
        <div class="header-actions">
          <!-- Category Analysis Reload (dev only: hidden in production) -->
          @if (showCategoryAnalysisReload) {
            <button 
              mat-stroked-button 
              color="primary"
              (click)="reloadCurrentTab()"
              class="category-analysis-reload-button"
              title="Reload (dev only)">
              <mat-icon>refresh</mat-icon>
              Reload
            </button>
          }
          <!-- Re-Audit Button for Audit Tabs -->
          @if (isAuditTab()) {
            <button 
              mat-raised-button 
              color="primary"
              (click)="triggerReAudit()"
              [disabled]="isReAuditLoading()"
              class="re-audit-header-button">
              @if (isReAuditLoading()) {
                <mat-spinner diameter="20" style="display: inline-block; margin-right: 8px;"></mat-spinner>
              } @else {
                <mat-icon>refresh</mat-icon>
              }
              Re-Audit
            </button>
          }
          <button class="mobile-tab-menu mobile-only" mat-icon-button [matMenuTriggerFor]="tabMenu"
            aria-label="Open tab menu">
            <mat-icon>menu</mat-icon>
          </button>
        </div>
      </div>
    }

    <!-- Global Tab Selection Menu (for mobile) -->
    <mat-menu #tabMenu="matMenu">
      @for (tab of getVisibleTabs(); track tab.id) {
        @if (tab.subTabs && tab.subTabs.length > 0) {
          <!-- Parent Tab with Sub-menu -->
          <button mat-menu-item [matMenuTriggerFor]="auditSubMenu">
            <mat-icon>{{ tab.icon }}</mat-icon>
            <span>{{ tab.name }}</span>
            <mat-icon>arrow_right</mat-icon>
          </button>
        } @else {
          <button mat-menu-item (click)="openTab(tab.id)">
            <mat-icon>{{ tab.icon }}</mat-icon>
            <span>{{ tab.name }}</span>
          </button>
        }
      }
    </mat-menu>
    
    <!-- Sub-menu for Audit tab (mobile) -->
    <mat-menu #auditSubMenu="matMenu">
      @for (subTab of getSubTabs('audit'); track subTab.id) {
        <button mat-menu-item (click)="openSubTab('audit', subTab.id)">
          <mat-icon>{{ subTab.icon }}</mat-icon>
          <span>{{ subTab.name }}</span>
        </button>
      }
    </mat-menu>


    <div class="tab-content">

      <!-- History loading: circle loader under tabs -->
      @if (historyLoading) {
        <div class="history-loading-placeholder">
          <app-common-circle-loader message="Loading version data..."></app-common-circle-loader>
        </div>
      }

      <!-- Global Loader with Progress Tracking -->
      <!-- Show loader if either individual tab is loading OR bulk search is in progress -->
      @else if (shouldShowGlobalLoader()) {
        <app-global-loader 
          [title]="getLoaderTitle()" 
          [isCustomLoader]="true"
          [showBackgroundButton]="true"
          [showSkipAuditsButton]="showSkipAuditsButton"
          [currentTaskId]="currentTaskId"
          [projectId]="projectId"
          (backgroundLoadClick)="onBackgroundLoadClick()"
          (skipAuditsClick)="onSkipAuditsClick()"
          (cancelClick)="onCancelClick()">
        </app-global-loader>
      }

      @else if (shouldShowInTabLoader()) {
        <div class="history-loading-placeholder">
          <app-common-circle-loader [message]="getInTabLoaderMessage()"></app-common-circle-loader>
        </div>
      }

      <!-- Failed State (Error or No Data) — Glossary does not use analysis tabData -->
      @else if (error || (activeTab && activeTab !== 'glossary' && !isLoading && !shouldShowGlobalLoader() && !hasDataForActiveTab())) {
        <div class="failed-state">
          <div class="failed-content">
            <mat-icon>{{ error ? 'error' : 'analytics' }}</mat-icon>
            <h3>{{ error ? 'Analysis Failed' : 'No Data Available' }}</h3>
            <p>{{ getFailureMessage() }}</p>
          </div>
        </div>
      }

      <!-- <p>activeTab: {{ activeTab }}</p>
      <p>isLoading: {{ isLoading }}</p>
      <p>tabData: {{ tabData }}</p>
      <p>error: {{ error }}</p> -->

      @else if (!isLoading && !shouldShowGlobalLoader() && !error && !historyLoading) {
      <div class="tab-container">
        @if (activeTab === 'overview') {
          <app-generic-overview #overviewCmp [selectedEntities]="selectedEntities"></app-generic-overview>
        }

        @if (activeTab === 'ai-citations') {
          <app-generic-ai-citations #citationsCmp>
          </app-generic-ai-citations>
        }

        @if (activeTab === 'category-analysis') {
          <app-generic-category-analysis-tab #categoryAnalysisCmp>
          </app-generic-category-analysis-tab>
        }

        @if (activeTab === 'prompts') {
          <app-generic-prompts></app-generic-prompts>
        }

        @if (activeTab === 'ai-visibility') {
          <div class="ai-visibility-tab-content">
            <app-generic-ai-visibility
              [llmComparisonData]="getAiVisibilityComparisonData()"
              [hideReloadButton]="false"
              [showReloadProgress]="true"
              (reloadMetrics)="reloadCurrentTab()">
            </app-generic-ai-visibility>
          </div>
        }

        @if (activeTab === 'sentiment-tracking') {
          <app-generic-sentiment-tracking #sentimentCmp>
          </app-generic-sentiment-tracking>
        }

        @if (activeTab === 'consumer-insights') {
          <app-generic-consumer-insights #consumerInsightsCmp>
          </app-generic-consumer-insights>
        }

        @if (activeTab === 'ai-content-index') {
          <app-generic-ai-content-index #aiContentIndexCmp>
          </app-generic-ai-content-index>
        }

        @if (activeTab === 'content-creation') {
          <app-generic-content-creation #contentCreationCmp>
          </app-generic-content-creation>
        }

        @if (activeTab === 'performance-tracking') {
          <app-generic-performance-tracking #performanceTrackingCmp>
          </app-generic-performance-tracking>
        }

        @if (activeTab === 'eeat-audit') {
          <app-generic-eeat-audit #eeatAuditCmp>
          </app-generic-eeat-audit>
        }

        @if (activeTab === 'landing-page-audit') {
          <app-generic-landing-page-audit #landingPageAuditCmp>
          </app-generic-landing-page-audit>
        }

        @if (activeTab === 'ai-audit') {
          <app-generic-ai-audit #aiAuditCmp>
          </app-generic-ai-audit>
        }

        @if (activeTab === 'prompt-research') {
          <app-generic-prompt-research #promptResearchCmp [selectedEntities]="selectedEntities">
          </app-generic-prompt-research>
        }

        @if (activeTab === 'prompt-tracking') {
          <app-generic-prompt-tracking #promptTrackingCmp [selectedEntities]="selectedEntities">
          </app-generic-prompt-tracking>
        }

        @if (activeTab === 'glossary') {
          <app-generic-glossary></app-generic-glossary>
        }
      </div>
      }

    </div>
  </div>
</div>